import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Breadcrumbs from "@/components/Breadcrumbs";
import PawDivider from "@/components/PawDivider";
import BenjiSeal from "@/components/BenjiSeal";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { EstimateCTASection, SectionHeading, ResponsibleDisposalSection } from "@/components/Sections";
import { EstimateButton } from "@/components/CTAButtons";
import { promises, business, assets } from "@/data/site";
import { CheckCircle2, PawPrint } from "lucide-react";

export default function About() {
  return (
    <>
      <Seo
        title="About Us | Benji's Hauling & Junk Removal"
        description="Benji's Hauling & Junk Removal was inspired by loyalty, dependability, and always being ready to help. Learn about our promise and the golden retriever behind the name."
        path="/about"
      />
      <Breadcrumbs items={[{ label: "About" }]} />
      <PageHero
        eyebrow="About"
        title="Junk Removal Inspired by Loyalty"
        sub="Named after a loyal golden retriever, Benji's Hauling was built on the qualities we love most about man's best friend: dependability, enthusiasm, and always being ready to help."
      />

      {/* Story */}
      <section className="bg-cream py-20">
        <div className="container-x">
          <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
            <Reveal>
              <div className="space-y-4 text-base leading-relaxed text-charcoal/75">
                <p>
                  Benji's Hauling &amp; Junk Removal was inspired by the qualities we love most about man's best
                  friend: loyalty, dependability, enthusiasm, and always being ready to help.
                </p>
                <p>
                  We wanted to create a junk removal company that customers feel comfortable calling, whether
                  they need one unwanted item gone or help tackling a full cleanout.
                </p>
                <p>
                  Our goal is simple: make getting rid of junk as easy and stress-free as possible while treating
                  our customers and their property with respect.
                </p>
              </div>
              <div className="mt-7">
                <BenjiSeal />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="overflow-hidden rounded-lg border border-border">
                <img
                  src="https://images.unsplash.com/photo-1614359835514-92f8ba196357?auto=format&fit=crop&w=800&q=72"
                  alt="Benji's crew loading a truck during a cleanout"
                  width="800" height="800" loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <PawDivider className="bg-cream" />

      {/* Our promise */}
      <section className="bg-accent/40 py-20">
        <div className="container-x">
          <Reveal>
            <SectionHeading eyebrow="What you can count on" title="Our Promise" align="center" />
          </Reveal>
          <StaggerGroup className="mx-auto mt-12 grid max-w-4xl gap-3 sm:grid-cols-2">
            {promises.map((p) => (
              <StaggerItem key={p} className="flex items-center gap-3 rounded-lg border border-border bg-white p-5">
                <CheckCircle2 className="h-5 w-5 shrink-0 text-mutedgreen" strokeWidth={2} />
                <span className="font-semibold text-forest">{p}</span>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Meet Benji */}
      <section className="bg-cream py-20">
        <div className="container-x">
          <div className="grid items-center gap-10 rounded-lg border border-border bg-white p-8 sm:p-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
            <Reveal className="relative">
              <div className="relative overflow-hidden rounded-lg border border-border bg-accent/40">
                <img
                  src={assets.benji}
                  alt="Benji, the golden retriever behind Benji's Hauling & Junk Removal"
                  width="700" height="900" loading="lazy"
                  className="h-full w-full object-cover"
                />
                <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-md bg-forest/90 px-2.5 py-1 text-xs font-bold text-cream">
                  <PawPrint className="h-3.5 w-3.5 text-gold" /> Meet Benji
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-gold-dark">
                <PawPrint className="h-3.5 w-3.5" strokeWidth={2.4} /> The good boy behind the name
              </span>
              <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-forest sm:text-4xl">Meet Benji</h2>
              <div className="mt-4 space-y-3 text-base leading-relaxed text-charcoal/75">
                <p>
                  Benji is a goofy and energetic golden retriever that loves to go swimming and chase sticks.
                  He loves going hiking, on car rides, and meeting new people. If you have food in your hand,
                  he'll listen to any command instantly.
                </p>
                <p>
                  Every customer with a dog at home can receive a complimentary treat bag with their booked
                  service, and Benji insists on it.
                </p>
              </div>
              <div className="mt-6">
                <EstimateButton label="Get Your Free Estimate" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <ResponsibleDisposalSection />
      <EstimateCTASection />
    </>
  );
}
